<template>
  <q-select
    v-if="type === SELECTOR_TYPES.DROPDOWN"
    :model-value="currentLanguage"
    bg-color="responsive"
    :label="$t('languages.actions.select')"
    :options="currentCircuit.availableLanguagesOptions"
    :display-value="$t(`languages.${currentLanguage}`)"
    emit-value
    map-options
    :dense="dense"
    :rounded="rounded"
    :disable="loading"
    data-test-id="language-selector"
    @update:model-value="changeAppLanguage"
  >
    <template
      v-if="!!labelStyle"
      #prepend
    >
      <q-icon
        v-if="labelStyle === LABEL_TYPE.ICON || labelStyle === LABEL_TYPE.ICON_ONLY"
        name="fal fa-language"
      />
      <country-flag
        v-if="labelStyle === LABEL_TYPE.FLAG || labelStyle === LABEL_TYPE.FLAG_ONLY"
        :country="getCountryFlagKeyForLanguage(currentLanguage)"
        size="normal"
        style="width: 40px; border-radius: 20px; margin-bottom: 2px;"
        :style="{ 'margin-right': labelStyle === LABEL_TYPE.FLAG ? '0' : undefined }"
      />
    </template>
    <template
      v-if="!!labelStyle"
      #option="{ itemProps, opt, toggleOption }"
    >
      <q-item v-bind="itemProps">
        <q-item-section
          avatar
          top
          class="col-auto"
          style="height: 40px;"
        >
          <country-flag
            :country="getCountryFlagKeyForLanguage(opt.value)"
            size="normal"
            style="width: 40px; border-radius: 20px;"
          />
        </q-item-section>
        <q-item-section @click="toggleOption(opt)">
          <q-item-label>{{ opt.label }}</q-item-label>
        </q-item-section>
      </q-item>
    </template>
  </q-select>
  <q-btn-dropdown
    v-else-if="type === SELECTOR_TYPES.BUTTON_DROPDOWN"
    :dense="dense"
    flat
    align="between"
    :ripple="false"
    :text-color="textColor"
    :loading="loading"
    :size="buttonSize"
    data-test-id="language-selector"
  >
    <template #label>
      <div class="row items-center no-wrap">
        <q-icon
          v-if="labelStyle === LABEL_TYPE.ICON || labelStyle === LABEL_TYPE.ICON_ONLY"
          :class="labelStyle !== LABEL_TYPE.ICON_ONLY && labelStyle !== LABEL_TYPE.FLAG_ONLY ? dense ? 'q-mr-xs' : 'q-mr-sm' : undefined"
          name="fal fa-language"
        />

        <country-flag
          v-if="labelStyle === LABEL_TYPE.FLAG || labelStyle === LABEL_TYPE.FLAG_ONLY"
          :country="getCountryFlagKeyForLanguage(currentLanguage)"
          size="normal"
          style="width: 40px; border-radius: 20px; margin-bottom: 2px;"
          :style="{ 'margin-right': labelStyle !== LABEL_TYPE.ICON_ONLY && labelStyle !== LABEL_TYPE.FLAG_ONLY ? '0' : undefined }"
        />
        <div
          v-if="labelStyle !== LABEL_TYPE.ICON_ONLY && labelStyle !== LABEL_TYPE.FLAG_ONLY"
        >
          {{ $t(`languages.${currentLanguage}`) }}
        </div>
      </div>
    </template>
    <q-list
      :bordered="false"
    >
      <q-item
        v-for="option in currentCircuit.availableLanguagesOptions"
        :key="option.value"
        v-close-popup
        clickable
        @click="changeAppLanguage(option.value)"
      >
        <q-item-section
          v-if="!!labelStyle"
          avatar
          top
          class="col-auto"
          style="height: 40px;"
        >
          <country-flag
            :country="getCountryFlagKeyForLanguage(option.value)"
            size="normal"
            style="width: 40px; border-radius: 20px;"
          />
        </q-item-section>
        <q-item-section>
          <q-item-label>{{ option.label }}</q-item-label>
        </q-item-section>
      </q-item>
    </q-list>
  </q-btn-dropdown>
  <q-btn-toggle
    v-else-if="type === SELECTOR_TYPES.BUTTON_TOGGLE || type === SELECTOR_TYPES.BUTTON_TOGGLE_FLAT"
    :options="currentCircuit.availableLanguagesOptions"
    :model-value="currentLanguage"
    :dense="dense"
    spread
    unelevated
    :flat="type === SELECTOR_TYPES.BUTTON_TOGGLE_FLAT"
    :ripple="false"
    :color="$q.dark.isActive ? 'responsive-grey-1' : 'responsive-grey-4'"
    :toggle-color="type === SELECTOR_TYPES.BUTTON_TOGGLE_FLAT ? undefined : 'primary'"
    :text-color="type === SELECTOR_TYPES.BUTTON_TOGGLE_FLAT ? 'responsive-grey-5' : 'responsive'"
    :toggle-text-color="type === SELECTOR_TYPES.BUTTON_TOGGLE_FLAT ? textColor : $isColorDark(currentSite.primaryColor) ? 'white' : 'black'"
    :disable="loading"
    :size="buttonSize"
    data-test-id="language-selector"
    @update:model-value="changeAppLanguage"
  />
</template>

<script>
import { changeLanguageAndReload, getCountryFlagKeyForLanguage } from 'src/i18n'
const SELECTOR_TYPES = {
  DROPDOWN: 'dropdown',
  BUTTON_DROPDOWN: 'button-dropdown',
  BUTTON_TOGGLE: 'button-toggle',
  BUTTON_TOGGLE_FLAT: 'button-toggle-flat'
}
const LABEL_TYPE = {
  ICON: 'icon',
  FLAG: 'flag',
  ICON_ONLY: 'icon-only',
  FLAG_ONLY: 'flag-only'
}
export default {
  name: 'LanguageSelector',

  props: {
    type: {
      type: String,
      default: SELECTOR_TYPES.DROPDOWN,
      required: false
    },
    buttonSize: {
      type: String,
      default: 'md',
      required: false
    },
    textColor: {
      type: String,
      default: null,
      required: false
    },
    labelStyle: {
      type: String,
      default: null,
      required: false
    },

    dense: {
      type: Boolean,
      default: false,
      required: false
    },
    rounded: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  context: ['currentUser'],
  configuration: ['currentCircuit', 'currentSite'],
  data () {
    return {
      loading: false
    }
  },
  computed: {
    currentLanguage () {
      return this.$i18n.localeLanguage
    }
  },
  created () {
    _.extend(this, { getCountryFlagKeyForLanguage, SELECTOR_TYPES, LABEL_TYPE })
  },
  methods: {
    async changeAppLanguage (newLanguage) {
      if (newLanguage !== this.currentLanguage) {
        this.loading = true
        await changeLanguageAndReload(newLanguage, this.currentUser)
        this.loading = false
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.flag {
  background-position-x: -6px !important; // to make center of flag match center of circle icon
}
</style>
